import { ClientBookingQuote } from "@/redux/apis/clientBooking/types";
import { ClientPaymentMethod } from "@/redux/apis/clientPayment/types";
import { PointsOfInterest } from "@/redux/apis/clientPoi/types";

/**
 * Which input the next location pick targets — a suggestion tap, the map pin's
 * "Select", or a reverse-geocode all write into `activeField`. Web equivalent of
 * the mobile booking flow's route-param (`StopType` + `index`).
 */
export type ActiveBookingField = { kind: "pickup" } | { kind: "destination" } | { kind: "stop"; index: number };

/**
 * The passenger a booking is for. `kind` distinguishes the account holder ("self"), a saved
 * favourite guest, and a one-off guest added during this session. Normalised (camelCase) so the
 * UI can render any source uniformly.
 */
export interface BookingPassenger {
  id: string;
  title?: string;
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  kind: "self" | "favourite" | "guest";
  /** A guest's link to the favourite (its uuid) they were booked from or match, kept with the booking. */
  favouriteId?: string | null;
}

export const SELF_PASSENGER_ID = "self";

export type PassengerContact = Omit<BookingPassenger, "id" | "kind">;

/**
 * Guest flow only. The contact details the guest enters at the "Who is this booking for?"
 * step. Unlike the authed flow (which reads the account holder / a favourite / a session guest via
 * `passenger`), the guest has no account — these details are the account-mint source and are sent as
 * the top-level `email`/`first_name`/`last_name`/`phone`/`title` on the guest booking-create body.
 */
export interface GuestContact {
  title: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
}

/**
 * Guest flow only: card metadata returned by Stripe's `createPaymentMethod` (`paymentMethod.card`),
 * captured alongside the pm_ so the confirmation screen can render a Payment box (Figma 5553-152145)
 * without a stored/saved card.
 */
export interface GuestCardDisplay {
  brand: string;
  last4: string;
  expMonth: number;
  expYear: number;
}

/**
 * Vehicle selection for the booking draft — mirrors the mobile `VehicleData`
 * (app-customer src/types/BookingData.ts). Owned by DEV-444; declared here so the
 * payload builder consumes it the moment that slice work lands.
 */
export type ClientBookingVehicle = {
  vehicle_id: number;
  uuid?: string;
  name?: string;
  max_passengers?: number;
  max_bags?: number;
};

/**
 * Transfer type selection (mobile `TransferTypeData`). Owned by DEV-444's Vehicle
 * and Transfer step; the create API REQUIRES it as `listed_events_list`
 * ("Invalid transfer type supplied" without it). The extra fields (`multiplier`,
 * `special_event`, `vehicle_exclusions`) let the eventual selection UI exclude
 * incompatible vehicles.
 */
export type ClientBookingTransferType = {
  id: number;
  label?: string;
  type?: string;
  multiplier?: string;
  special_event?: boolean;
  vehicle_exclusions?: string[];
};

/** Booking extra selection — mirrors mobile `ExtrasData`. */
export type ClientBookingExtra = {
  extra_id: string;
  name?: string;
  amount?: number;
  /**
   * How many of this extra. Optional so drafts saved before quantities existed still read as one.
   * The wire has no quantity field: the payload repeats the line item instead, which is how the
   * backend counts them (JobAnalyzer OptionFactory tallies duplicate `line-item` entries).
   */
  quantity?: number;
};

/**
 * One row in the Other Details step's "Passenger Details" list. Row count follows the Vehicle &
 * Transfer passenger count; row 0 is seeded from the selected passenger. Draft + summary only.
 */
export type BookingPassengerDetail = { name: string; phone: string };

/**
 * Why the pickup names a terminal the client did not pick — the flight it was derived from, so the
 * badge can say "Pickup updated to Terminal 3 / Based on QF994 arriving …". The terminal is held as the
 * lookup returned it (`"3"`, `"T3"`), formatted for display by `formatTerminal`.
 */
export type PickupTerminalSource = { flightNumber: string; terminal: string; arrivalIso: string };

/**
 * The drop-off twin (DEV-1487): why the DROP-OFF names a terminal the client did not pick — the
 * departure flight it was derived from, and when that flight leaves.
 *
 * A separate type rather than a widened `PickupTerminalSource`, because the instant means a different
 * event on each side and the summary sentence names it ("arriving …" against "departing …"). One shape
 * with an `iso` the reader has to interpret is how the two sentences end up saying the same word.
 */
export type DepartureTerminalSource = { flightNumber: string; terminal: string; departureIso: string };

/**
 * How the committed pickup TIME was arrived at, once a departure flight is on the booking.
 *
 * `"suggested"` — the SUGGESTED PICKUP the server computed was applied as-is. `"manual"` — the client
 * set the time themselves, so a re-derivation must never overwrite it. `null` — nothing has been
 * committed against the current departure flight yet, which is what lets a NEW flight re-apply its own
 * suggestion: `setDepartureFlightNumber` resets it on a real change.
 *
 * Only meaningful alongside `departureFlightNumber`. The ARRIVAL derivation does not consult it — that
 * path anchors on `pickupFlightLandingUsed` and its own touched-terms, and it outranks the suggestion.
 */
export type PickupTimeSource = "suggested" | "manual";

export interface ClientBookingState {
  pickup: PointsOfInterest | null;
  destination: PointsOfInterest | null;
  /** Intermediate stops, in order. `null` = an added-but-not-yet-filled stop input. */
  stops: (PointsOfInterest | null)[];
  activeField: ActiveBookingField | null;
  /** True while the full-bleed map pin picker is open for `activeField`. */
  isPinning: boolean;
  /**
   * Flight number captured for an airport pickup. Free text, no lookup here.
   * Cleared whenever the pickup changes — mirrors the mobile booking reset.
   */
  pickupFlightNumber: string | null;
  /**
   * "Delay driver arrival by" — minutes the passenger asked the driver to wait after the flight is
   * at the gate. Sent as `flight_pickup_delay_minutes` and stored on the job, because the flight
   * arrival policy recomputes `pickup = flight arrival + this` server-side (DEV-1429). `null` means
   * never asked (no flight, or a manually entered time); `0` is a real answer — meet on arrival.
   * Cleared with `pickupFlightNumber` whenever the pickup changes.
   */
  pickupFlightDelayMinutes: number | null;
  /**
   * The landing the COMMITTED pickup was derived from, as the instant to send as `flight_landing_used`
   * (see `flightLandingUsedIso`). Written only when the pickup was actually built from a resolved
   * landing in this session, so the pair the server verifies is the pair it was given: it holds the
   * exact operand of `pickup = landing + delay`, not whatever the flight has since drifted to.
   *
   * `null` for a manually entered time, for a booking with no flight, and for an edit that re-commits an
   * unchanged pickup — in that last case the server skips provenance entirely (the pickup did not move),
   * so there is nothing to prove and a live landing would only misdescribe the booked time.
   *
   * Cleared with `pickupFlightNumber` whenever the pickup changes.
   */
  pickupFlightLandingUsed?: string | null;
  /**
   * The arrival the committed pickup was derived from, as stored. Paired with `pickupDatetime`: while
   * the panel is showing the booked pickup it shows this arrival, and the moment the pickup re-derives
   * it shows the live one instead. The pair moves together, so the sum always reconciles (D14).
   */
  pickupFlightDerivedArrival?: string | null;
  /** Raw stored source of that arrival, mapped to a LandingSource in the panel that renders it. */
  pickupFlightDerivedArrivalSource?: string | null;
  /** When the engine froze the pickup. Gates the lock pill; nothing else may. */
  pickupFlightFrozenAt?: string | null;
  /** Why the engine froze the pickup. Needs no flight lookup, so it discloses in view-only too. */
  pickupFlightFrozenReason?: string | null;
  /**
   * The flight number changed after a pickup was committed, so the committed pickup was derived from a
   * flight that is no longer selected. Set by `setPickupFlightNumber` on a real change; cleared the moment
   * a pickup is committed.
   *
   * Exists because the Date & Time panel ANCHORS to the committed pickup rather than re-deriving (DEV-1499),
   * and a new flight is an operand that has to reopen that. The panel's own `flightTouched` term reads the
   * ORIGINAL booking's flight number, which only exists on the edit path — on create there is no baseline
   * to compare against, so without this flag a flight changed after Proceed would commit the previous
   * flight's pickup.
   *
   * Replaces nulling `pickupDatetime` for the same purpose (DEV-1510). That forced the re-derivation, but it
   * took the booked DATE with it: the calendar fell back to today, and a client whose replacement flight
   * does not operate today was told "we couldn't find flight X on that date" immediately after a successful
   * flight change. The date is what the client chose; only the derivation needed to re-run.
   */
  pickupFlightChangedSinceCommit?: boolean;
  pickupNotFlying: boolean;
  /**
   * Set when the flight lookup MOVED the pickup to its arrival terminal, and read only to explain that
   * move in the Booking Summary's Address card (DEV-1594). Null whenever the pickup is the client's own
   * choice — including every path that clears the flight or replaces the pickup — so the badge is
   * present exactly while there is something to disclose.
   */
  pickupTerminalSource?: PickupTerminalSource | null;
  /**
   * DEPARTURE FLIGHT — the flight the passenger is catching from an airport DROP-OFF (DEV-1487). Free
   * text, no lookup here, exactly like `pickupFlightNumber`. Cleared whenever the destination changes.
   *
   * Deliberately its own field rather than a widening of the pickup one: a booking can carry both (an
   * airport-to-airport transfer), the pickup engine reads only the arrival flight, and the two clear on
   * different events.
   */
  departureFlightNumber?: string | null;
  /**
   * The departure flight changed after a pickup was committed. Twin of `pickupFlightChangedSinceCommit`,
   * and read for the same reason: the Date & Time panel anchors on the committed pickup, so a new
   * departure flight has to reopen the SUGGESTED PICKUP that the previous one produced.
   */
  departureFlightChangedSinceCommit?: boolean;
  /** Set when the lookup MOVED the drop-off to the flight's departure terminal — the summary's badge. */
  departureTerminalSource?: DepartureTerminalSource | null;
  /** See {@link PickupTimeSource}. Null until a pickup is committed against the current departure flight. */
  pickupTimeSource?: PickupTimeSource | null;
  /**
   * Set only by the picker's "New Guest" CTA (DEV-1610): this booking was started to add a new guest,
   * so the Passenger sub-panel opens straight in its new-guest form when the EA reaches Trip Details.
   * A draft flag rather than an entry-point check so no other route into the sheet changes (mirrors
   * the draft-state-over-entry-point choice in DEV-1487). Spent once the passenger step consumes it.
   */
  startNewGuest?: boolean;
  /** Selected passenger. `null` ⇒ the account holder ("self") is selected by default. */
  passenger: BookingPassenger | null;
  /** Guests added via "New Guest" that weren't saved to favourites — session-only (lost on refresh). */
  sessionGuests: BookingPassenger[];
  passengerContactOverrides?: Record<string, PassengerContact>;
  /** Pickup date + time as an ISO string (DEV-443 date/time panel). `null` until picked. Sent as `pickup_datetime`. */
  pickupDatetime: string | null;
  /**
   * Payment method selected for THIS booking only (never PATCHes the account
   * default — DEV-1152 semantics untouched). Auto-preselected to the account
   * default when payment methods load and nothing is selected yet.
   */
  paymentMethod: ClientPaymentMethod | null;
  /**
   * Guest flow only: Stripe PaymentMethod id (`pm_…`) captured client-side via
   * `stripe.createPaymentMethod`. Null until the guest enters a card. The authed flow leaves this
   * null and uses `paymentMethod` (saved-card/invoice selection) instead.
   */
  guestPaymentMethodId?: string | null;
  /**
   * Guest flow only: the 2-letter ISO country of the captured card (Stripe's
   * `paymentMethod.card.country`, e.g. "PH"/"AU"; may be null if Stripe doesn't return one). Feeds
   * `payment_method_country` on the guest quote so the Price Summary reflects the card's local/
   * international processing-fee rate. Always cleared together with `guestPaymentMethodId`.
   */
  guestPaymentMethodCountry?: string | null;
  /**
   * Guest flow only: display metadata (brand/last4/expiry) for the captured card, from Stripe's
   * `createPaymentMethod` response. Feeds the confirmation screen's Payment box. Always cleared
   * together with `guestPaymentMethodId`.
   */
  guestPaymentMethodCard?: GuestCardDisplay | null;
  /**
   * Guest flow only: the guest's own contact details captured at the passenger step.
   * Null in the authed flow, which reads `passenger` instead. Feeds the account-mint fields on submit.
   */
  guestContact?: GuestContact | null;
  /**
   * Account the draft is being built for: the managed client's id under an operator
   * OBO booking, undefined/null in self-service. Not part of the booking payload —
   * carried only so the sheet can reset a draft that was started for a different
   * scope (DEV-462 draft hygiene). Compared on mount; a mismatch clears the draft.
   */
  scopeAccountId?: string | null;

  // --- Forward-compat draft fields (not yet settable from the UI) ---
  // Declared optional so the payload builder lights up automatically as the
  // owning tickets land; until then they read as undefined and gate booking.

  /** Selected vehicle — DEV-444 owns the UI. */
  vehicle?: ClientBookingVehicle | null;
  /** Selected transfer type (`listed_events_list`) — DEV-444 owns the UI; REQUIRED by the create API. */
  transferType?: ClientBookingTransferType | null;
  /** Passenger count — DEV-444 Vehicle and Transfer step. Builder defaults to 1. */
  numOfPassengers?: number;
  /** Declared bag count — DEV-444. Sent as `vehicles[0].bags`; the backend column is nullable. */
  numOfBags?: number;
  /** Selected extras — DEV-446 owns the UI. Builder defaults to []. */
  extras?: ClientBookingExtra[];
  /** Notes sent as `additional_info` — DEV-446 Other Details step. Builder defaults to "". */
  additionalInfo?: string;
  /** Free-text "Booked by" (DEV-446). Defaults to the account holder's name in the UI. */
  bookedBy?: string;
  /** Client reference (DEV-446). */
  reference?: string;
  /** Per-passenger name/phone rows (DEV-446 Other Details). Draft + summary only. */
  passengers?: BookingPassengerDetail[];
  /** Job rate type ("journey_rate" | "hourly_rate" | "daily_rate" | "fixed_*"). Builder defaults to journey. */
  jobRate?: string;
  /** Duration for hourly/daily rates. Builder defaults to 0 (mobile parity). */
  bookedForDuration?: number;
  fixedPrice?: number;
  /**
   * View-only sheet only (DEV-446): true once a closed booking has been hydrated into the draft.
   * Marks the draft as a read-only replay — used to gate the leave-guard, the payment auto-preselect,
   * and the loading state. Reliable regardless of pricing (unlike storedQuote, which is null when the
   * operator hides pricing). False/undefined in every editable/create flow.
   */
  viewOnly?: boolean;
  /**
   * The replayed booking's stored validation flag (`job.job_verified`) — undefined for a fresh draft,
   * and for a backend that predates the field.
   *
   * `false` is the "Requires Validation" the bookings table flags in red. Distinct from a waypoint
   * having no coordinates: that is what the MAP filters on, and the two coincide in practice but
   * answer different questions.
   */
  isJobVerified?: boolean;
  /**
   * View-only sheet only (DEV-446): the finished booking's stored price, snapshotted from the detail
   * response. When set, the Price Summary renders this instead of firing a live re-quote (a closed
   * booking must show what was charged, not today's price). Null when the operator hides pricing.
   */
  storedQuote?: ClientBookingQuote | null;
  /**
   * Edit sheet only: the job id being edited. Set when an editable booking is hydrated for editing;
   * drives the "Update Booking" CTA (over "Book Trip") and is the PUT /v2/cust/booking/:jobId target.
   * Null/undefined in every create flow. Distinct from `viewOnly` — an edit draft stays fully editable.
   */
  editJobId?: number | null;
  /**
   * Edit sheet only: an immutable snapshot of the editable draft AS HYDRATED from the booking (incl.
   * the resolved payment method). Diffed against the live draft to build the change-review table and
   * to gate the CTA ("no changes ⇒ nothing to update"). Set together with `editJobId`.
   */
  editOriginal?: Partial<ClientBookingState> | null;
}
